var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import get from 'lodash.get';
import merge from 'lodash.merge';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../abstractions/hooks/useTranslation';
import useDesign from '../../../hooks/useDesign';
var LINE_BREAK_DIVIDER = '\n';
/**
 * Checks whether the value provided is a valid var name. E.g.: var(--some-name)
 * @param name
 * @returns
 */
function getCSSVar(name) {
    var regexMatches = name.match(/^var\((--\S+)\)$/);
    // Match has to be length 2: first global match and second group match.
    if ((regexMatches === null || regexMatches === void 0 ? void 0 : regexMatches.length) === 2)
        return getComputedStyle(document.documentElement).getPropertyValue(regexMatches[1]);
    return name;
}
/**
 * Goes through all provided props and gets the related CSS Var if valid
 * @param prop
 * @returns
 */
function parseCSSVars(prop) {
    if (!prop)
        return prop;
    if (Array.isArray(prop))
        return prop.map(function (p) { return getCSSVar(p); });
    return getCSSVar(prop);
}
export var withDataParsing = function (WrappedChart) {
    var ChartWithData = function (_a) {
        var design = _a.design, propsLabels = _a.labels, propsDatasets = _a.datasets, values = _a.values;
        var _b = useState(null), chartData = _b[0], setChartData = _b[1];
        var config = useDesign(design).config;
        var designLabels = config.labels, designDatasets = config.datasets, pick = config.pick, labelsTemplate = config.labelsTemplate, sourceParent = config.sourceParent, labelsAttr = config.labelsAttr;
        var translate = useTranslation().translate;
        var translateWithLinebreaks = useCallback(function (i18n) { return translate(i18n).split(LINE_BREAK_DIVIDER); }, [translate]);
        var translateLabels = useCallback(function (labels) {
            return labels.map(function (_a) {
                var id = _a.id, fallback = _a.fallback;
                return translateWithLinebreaks({ id: id, fallback: fallback });
            });
        }, [translateWithLinebreaks]);
        var translateDatasetLabels = useCallback(function (datasets) {
            return datasets.map(function (_a) {
                var label = _a.label, rest = __rest(_a, ["label"]);
                return (__assign({ label: translateWithLinebreaks(label) }, rest));
            });
        }, [translateWithLinebreaks]);
        var generateLabels = useCallback(function (labelsTemplate, length) {
            if (!labelsTemplate || !length)
                return [' '];
            var id = labelsTemplate.id, fallback = labelsTemplate.fallback, _a = labelsTemplate.startingValue, startingValue = _a === void 0 ? 0 : _a;
            var index = startingValue;
            return Array.from({ length: length }, function (_) {
                var translation = translateWithLinebreaks({
                    id: id,
                    fallback: fallback,
                    values: { index: index },
                });
                index += 1;
                return translation;
            });
        }, [translateWithLinebreaks]);
        var parseDataValues = useCallback(function (data) {
            if (Array.isArray(data))
                return data.map(function (value) {
                    return typeof value === 'string' ? parseFloat(value.replaceAll(',', '')) : value;
                });
            return typeof data === 'string' ? [parseFloat(data.replaceAll(',', ''))] : [data];
        });
        var parsePickConfiguration = useCallback(function () {
            /** Datasets: **/
            var pickedDatasets = [];
            var scopedValues = sourceParent ? get(values, sourceParent) : values;
            pick.forEach(function (p) {
                var label = p.label, backgroundColor = p.backgroundColor, borderColor = p.borderColor, source = p.source, _a = p.prependData, prependData = _a === void 0 ? [] : _a, _b = p.appendData, appendData = _b === void 0 ? [] : _b, rest = __rest(p, ["label", "backgroundColor", "borderColor", "source", "prependData", "appendData"]);
                var data = [];
                // If sourceParent is provided, means the data is going to be nested inside an object.
                // We will need to go through values and extract.
                // Example: { sourceParent: { key: { source: 1 }, key2: { source: 2 } } }
                if (sourceParent && scopedValues)
                    data = Object.values(scopedValues).map(function (value) { return get(value, source); });
                // Otherwise, the data is going to be plain to just take it.
                // Example: { source: [1,2,3] }
                else
                    data = get(scopedValues, source, []);
                var colors = {};
                if (backgroundColor)
                    colors['backgroundColor'] = parseCSSVars(backgroundColor);
                if (borderColor)
                    colors['borderColor'] = parseCSSVars(borderColor);
                pickedDatasets.push(__assign(__assign({ label: translate(label), data: prependData.concat(parseDataValues(data)).concat(appendData) }, colors), rest));
            });
            if (pickedDatasets.length === 0)
                return {};
            /** Labels **/
            // If sourceParent and labelsAttr are provided, means the labels are going to be nested inside an object.
            // Example: { sourceParent: { key: { label: 'a label' }, anotherKey: { label: 'another label' } } }
            if (labelsAttr && sourceParent)
                data.labels = Object.values(scopedValues).map(function (value) { return get(value, labelsAttr); });
            var labels = data.labels.length
                ? data.labels
                : generateLabels(labelsTemplate, pickedDatasets[0].data.length);
            return { labels: labels, datasets: pickedDatasets };
        });
        var data = useMemo(function () { return ({
            labels: translateLabels(merge([], propsLabels, designLabels)),
            datasets: translateDatasetLabels(merge([], propsDatasets, designDatasets)),
        }); }, [propsLabels, propsDatasets, designLabels, designDatasets]);
        useEffect(function () {
            if (data.labels.length && data.datasets.length) {
                setChartData(data);
                return;
            }
            if (!pick)
                return;
            var _a = parsePickConfiguration(), labels = _a.labels, datasets = _a.datasets;
            setChartData({ labels: labels, datasets: datasets });
        }, [values]);
        if (!chartData)
            return null;
        return _jsx(WrappedChart, __assign({ design: design }, chartData));
    };
    ChartWithData.displayName = 'Chart';
    return ChartWithData;
};
